import { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Monitor, Users, Award, Heart, Wifi, Smile } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const stats = [
  { key: 'about.stat1', labelKey: 'about.stat1.label', value: 5000, suffix: '+' },
  { key: 'about.stat2', labelKey: 'about.stat2.label', value: 12, suffix: '+' },
  { key: 'about.stat3', labelKey: 'about.stat3.label', value: 15, suffix: '+' },
  { key: 'about.stat4', labelKey: 'about.stat4.label', value: 98, suffix: '%' },
];

const features = [
  { key: 'about.feature1', icon: Monitor },
  { key: 'about.feature2', icon: Users },
  { key: 'about.feature3', icon: Award },
  { key: 'about.feature4', icon: Heart },
  { key: 'about.feature5', icon: Wifi },
  { key: 'about.feature6', icon: Smile },
];

function AnimatedCounter({ value, suffix }: { value: number; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <span ref={ref} className="text-4xl md:text-5xl font-bold text-cp-orange">
      {count}{suffix}
    </span>
  );
}

export default function About() {
  const { t } = useLanguage();

  return (
    <section id="about" className="section-padding bg-cp-blue-dark relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-cp-orange/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -left-32 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('about.title')}
            </h2>
            <p className="text-xl text-cp-orange font-semibold mb-6">
              {t('about.subtitle')}
            </p>
            <p className="text-gray-300 leading-relaxed mb-8">
              {t('about.desc')}
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="glass rounded-xl p-4 text-center hover:bg-white/5 transition-colors"
                  >
                    <Icon className="w-6 h-6 text-cp-orange mx-auto mb-2" />
                    <span className="text-sm text-gray-300">{t(feature.key)}</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-2xl p-6 text-center"
              >
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                <p className="text-gray-400 mt-2">{t(stat.labelKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}